<template>
  <div v-if="userDocuments" class="relative">
    <div class="mb-4 rounded-md bg-white px-4 py-1">
      <div
        v-for="(document, i) in userDocuments"
        :key="i"
        class="relative last:border-b-0"
        :class="{
          'border-b': i !== userDocuments.length - 1 || !immutable,
        }"
      >
        <div class="relative flex items-center gap-4 pb-3 pt-3">
          <div class="basis-1/2 md:basis-[25%]">{{ document.name }}</div>
          <div class="basis-1/2 md:basis-[20%]">
            <span class="rounded-sm bg-obsidian-100 px-2 py-0.5 text-obsidian-800">{{
              t(`userDocumentTypes.${document.type as string}`)
            }}</span>
          </div>
          <div class="hidden basis-[42%] md:block">
            {{ document.comment ? shortenText(document.comment, 70) : '-' }}
          </div>
          <div class="hidden basis-[20%] justify-end md:block">
            <CVButton variant="ghost" @click.prevent="downloadDocument(document)">{{
              t('download')
            }}</CVButton>
          </div>
          <DropdownDotted
            :buttons="dropdownButtons(document)"
            data-testid="user-documents-dropdown"
          />
        </div>
      </div>
      <div v-if="userDocuments.length === 0" class="pb-3 pt-3">
        {{ t('noDocuments') }}
      </div>
    </div>

    <div v-if="!immutable">
      <UserDocumentsForm
        v-if="addingDocument"
        :user="user"
        :user-documents="userDocuments"
        @complete="addingDocument = false"
      />
      <div v-else class="flex justify-end">
        <CVButton @click.prevent="addDocument">
          {{ t('addDocument') }}
        </CVButton>
      </div>
      <div
        v-if="isRemovingUserDocument"
        class="absolute inset-[-10px] flex items-center justify-center bg-black bg-opacity-30"
      >
        <Spinner />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import DropdownDotted from '@/components/DropdownDotted.vue';
import type { User } from '@/entities/auth/user.entity';
import { shortenText } from '@/hooks/use-shorten-text';
import { useRemoveUserDocument, useUserDocuments } from '@/queries/use-user-documents';
import { Alert } from '@/services/alert.service';
import { downloadBlob, fetchBlobFromUrl } from '@/utils/download-file';
import type { UserDocumentDto, UserDocumentType } from '@carvia/ros-client-types';
import { flatMap } from 'lodash';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import UserDocumentsForm from './UserDocumentsForm.vue';

const props = defineProps<{
  user: User;
  immutable?: boolean;
  documentTypeFilter?: UserDocumentType[];
}>();

const { t } = useI18n();

const addingDocument = ref(false);

const { data: userDocumentsData } = useUserDocuments(props.user.id, {
  type: props.documentTypeFilter,
});
const { mutateAsync: removeUserDocument, isPending: isRemovingUserDocument } =
  useRemoveUserDocument();

const addDocument = () => {
  if (addingDocument.value) return;
  addingDocument.value = true;
};

const removeDocument = async (documentId: string) => {
  const alertResult = await Alert.fire({
    titleText: t('reallyDeleteDocumentTitle'),
    text: t('reallyDeleteDocumentText'),
    icon: 'question',
    showConfirmButton: false,
    showDenyButton: true,
    denyButtonText: t('delete'),
    showCancelButton: true,
    cancelButtonText: t('keep'),
  });

  if (alertResult.isDenied) {
    removeUserDocument({ userId: props.user.id, documentId });
  }
};

const downloadDocument = async (document: UserDocumentDto) => {
  if (!document.documentFileUrl) return;
  const blob = await fetchBlobFromUrl(document.documentFileUrl);
  const extension = document.documentFileKey?.split('.').pop() ?? '';
  const fileName = extension ? `${document.name}.${extension}` : document.name;
  await downloadBlob(blob, fileName);
};

const userDocuments = computed(() =>
  flatMap(
    userDocumentsData.value?.pages,
    (page) =>
      page.items.map((user) => ({
        ...user,
      })) ?? [],
  ),
);

const dropdownButtons = (document: UserDocumentDto) => [
  {
    title: t('download'),
    onClick: () => downloadDocument(document),
  },
  ...(!props.immutable
    ? [
        {
          title: t('delete'),
          onClick: () => removeDocument(document.id),
        },
      ]
    : []),
  ...(document.comment
    ? [
        {
          title: t('viewComment'),
          onClick: () => {
            Alert.fire({
              text: document.comment ?? '',
              icon: 'info',
              confirmButtonText: t('close'),
            });
          },
        },
      ]
    : []),
];
</script>

<i18n lang="json">
{
  "en": {
    "download": "Download",
    "addDocument": "+ Add Document",
    "reallyDeleteDocumentTitle": "Really remove Document?",
    "reallyDeleteDocumentText": "The File will be permanently deleted.",
    "keep": "Keep",
    "noDocuments": "This user has no documents",
    "viewComment": "View Comment",
    "close": "Close"
  },
  "de": {
    "download": "Download",
    "addDocument": "+ Dokument hinzufügen",
    "reallyDeleteDocumentTitle": "Dokument wirklich entfernen?",
    "reallyDeleteDocumentText": "Die Datei wird unwiderruflich gelöscht.",
    "keep": "Behalten",
    "noDocuments": "Dieser Benutzer hat noch keine Dokumente",
    "viewComment": "Kommentar anzeigen",
    "close": "Schließen"
  },
  "es": {
    "download": "Descargar",
    "addDocument": "+ Añadir documento",
    "reallyDeleteDocumentTitle": "¿Eliminar el documento?",
    "reallyDeleteDocumentText": "El archivo se eliminará de forma definitiva.",
    "keep": "Conservar",
    "noDocuments": "Este usuario no tiene documentos",
    "viewComment": "Ver comentario",
    "close": "Cerrar"
  }
}
</i18n>
